/**
 * Flattens nested JSON objects
 * Taken from http://jsfiddle.net/f6vpfter/ and
 * https://code.amazon.com/packages/CloudTuneHveUI/blobs/mainline/--/src/app/util/json-flattener.ts
 */
export class JsonFlattener {
    /**
     * Flattens the nested json data object
     * @param data Object to be flattened
     * @param maxDepth the maximum depth upto which the object can be flattened
     * @param maxWidth the maximum amount of key/properties to display at each level of the object.
     */
    static flatten(data, maxDepth = 10, maxWidth = 100) {
        const MAX_DEPTH_LIMIT = 100;
        const MAX_WIDTH_LIMIT = 100;
        if (maxDepth > MAX_DEPTH_LIMIT) {
            throw new Error(`Maximum depth of ${maxDepth} for JsonFlattener is greater than the allowed limit: ${MAX_DEPTH_LIMIT}`);
        }
        if (maxWidth > MAX_WIDTH_LIMIT) {
            throw new Error(`Maximum width of ${maxWidth} for JsonFlattener is greater than the allowed limit: ${MAX_WIDTH_LIMIT}`);
        }
        const result = {};
        const widthLimiter = new Map();
        function recurse(cur, prefix, depth) {
            // Check if maxDepth has been reached or if current element is a function or if react element
            if (depth >= maxDepth || cur instanceof Function || JsonFlattener.isReactElement(cur)) {
                if (JsonFlattener.isReactElement(cur)) {
                    console.log(`JsonFlattener detected React element at depth: ${depth}. Terminating early.`);
                }
                return;
            }
            //Checks whether this is not an object. If cur is falsy, if predicate returns true
            if (Object(cur) !== cur) {
                const curPrefix = prefix.split('.');
                const rootPrefix = curPrefix.splice(0, curPrefix.length - 1).join('.');
                if (widthLimiter.has(rootPrefix)) {
                    const width = widthLimiter.get(rootPrefix);
                    widthLimiter.set(rootPrefix, width ? width + 1 : 0);
                    if (width && width >= maxWidth) {
                        return;
                    }
                }
                else {
                    widthLimiter.set(rootPrefix, 1);
                }
                result[prefix] = cur;
            }
            else if (Array.isArray(cur)) {
                const l = cur.length;
                for (let i = 0; i < l; i++) {
                    recurse(cur[i], prefix + '_' + i + '_', depth + 1);
                }
                // Put empty array string if it is an empty array.
                if (l === 0) {
                    result[prefix] = '[]';
                }
            }
            else {
                let isEmpty = true;
                // safe to cast as we know that this is an Object, but not an array nor null
                const typedCur = cur;
                for (const p in typedCur) {
                    isEmpty = false;
                    recurse(typedCur[p], prefix ? prefix + '.' + p : p, depth + 1);
                }
                // Put empty string if it is an empty object.
                if (isEmpty && prefix) {
                    result[prefix] = '';
                }
            }
        }
        recurse(data, '', 0);
        return result;
    }
    static isReactElement(element) {
        return (typeof element === 'object' &&
            element !== null &&
            typeof element.$$typeof === 'symbol' &&
            element.$$typeof.toString() === 'Symbol(react.element)');
    }
}
//# sourceMappingURL=JsonFlattener.js.map